import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Params, QueryParamsHandling, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-button',
  imports: [CommonModule, TranslateModule, RouterLink],
  template: `
    <button
      [type]="isLink ? 'button' : type"
      [routerLink]="isLink ? routerLink : null"
      [queryParams]="isLink ? queryParams : undefined"
      [queryParamsHandling]="isLink ? queryParamsHandling : undefined"
      [fragment]="isLink ? fragment : undefined"
      [class]="getClasses() + ' ' + customClass"
      [disabled]="disabled || isLoading"
      (click)="onClick($event)"
      class="relative inline-flex items-center justify-center border focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200"
    >
      @if (isLoading) {
        <svg
          class="absolute animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      }
      <span class="inline-flex items-center justify-center" [class.invisible]="isLoading">
        <ng-content></ng-content>
      </span>
    </button>
  `,
  host: {
    '[class.w-full]': 'fullWidth',
    '[class.inline-block]': '!fullWidth',
    '[class.block]': 'fullWidth',
  },
  styles: [`
    :host {
      display: inline-block;
    }
    :host.w-full {
      display: block;
    }
    button {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  `],
})
export class ButtonComponent {
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() variant: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | 'accent' = 'primary';
  @Input() size: 'sm' | 'md' | 'lg' = 'md';
  @Input() disabled: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() loadingLabel = '';
  @Input() fullWidth: boolean = false;
  @Input() rounded: 'md' | 'xl' | '2xl' | '3xl' | '4xl' | 'full' = 'full';
  @Input() shadow: boolean = true;
  @Input() customClass: string = '';
  /** When set, navigates via RouterLink on the inner button. */
  @Input() routerLink: string | readonly any[] | null = null;
  @Input() queryParams: Params | undefined = undefined;
  @Input() queryParamsHandling: QueryParamsHandling | undefined = undefined;
  @Input() fragment: string | undefined = undefined;
  @Output() btnClick = new EventEmitter<Event>();

  get isLink(): boolean {
    if (this.routerLink == null) return false;
    if (typeof this.routerLink === 'string') return this.routerLink.length > 0;
    return Array.isArray(this.routerLink) && this.routerLink.length > 0;
  }

  getClasses(): string {
    let classes = '';

    switch (this.variant) {
      case 'primary':
        classes +=
          ' border-transparent text-white bg-primary hover:bg-blue-900 focus:ring-primary' + (this.shadow ? ' shadow-md' : '');
        break;
      case 'secondary':
        classes +=
          ' border-gray-200 text-gray-700 bg-white hover:bg-gray-50 focus:ring-gray-200';
        break;
      case 'accent':
        classes +=
          ' border-transparent text-white bg-accent hover:bg-opacity-90 focus:ring-accent' + (this.shadow ? ' shadow-md' : '');
        break;
      case 'outline':
        classes += ' border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50 focus:ring-primary';
        break;
      case 'ghost':
        classes += ' border-transparent text-gray-600 bg-transparent hover:bg-gray-100 focus:ring-gray-200';
        break;
      case 'danger':
        classes += ' border-transparent text-white bg-red-600 hover:bg-red-700 focus:ring-red-500';
        break;
    }

    switch (this.size) {
      case 'sm':
        classes += ' px-3.5 py-1.5 text-xs font-bold tracking-wide';
        break;
      case 'md':
        classes += ' px-5 py-2.5 text-sm font-bold tracking-wide';
        break;
      case 'lg':
        classes += ' px-6 py-3 text-base font-extrabold tracking-wide';
        break;
    }

    switch (this.rounded) {
      case 'md':
      case 'xl':
      case '2xl':
      case '3xl':
      case '4xl':
      case 'full':
        classes += ' rounded-full';
        break;
    }

    if (this.disabled || this.isLoading) {
      classes += ' opacity-50 cursor-not-allowed pointer-events-none';
    } else {
      classes += ' cursor-pointer transition-all active:scale-95';
    }

    if (this.fullWidth) {
      classes += ' w-full';
    }

    return classes;
  }

  onClick(event: Event) {
    if (this.disabled || this.isLoading) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    this.btnClick.emit(event);
  }
}
